// models/api/webhooks.ts
import { getRequest, postRequest, putRequest, deleteRequest } from './rest'
import { ALL_ORGS_SELECTED } from 'lib/constants'
import type { Webhook, WebhookDirection, WebhookWithSecret } from 'lib/types/webhooks'

const orgHeader = (orgId: string) => (orgId === ALL_ORGS_SELECTED ? {} : { org_id: orgId })

const toDate = (v: unknown): Date | undefined => (v ? new Date(v as any) : undefined)

const normalize = (w: any): Webhook => ({
  id: String(w?.id ?? ''),
  orgId: w?.orgId ?? w?.org_id ?? '',
  name: w?.name ?? '',
  url: w?.url ?? '',
  direction: (w?.direction ?? 'unidirectional') as WebhookDirection,
  enabled: w?.enabled ?? true,
  insertedAt: toDate(w?.insertedAt ?? w?.inserted_at),
  updatedAt: toDate(w?.updatedAt ?? w?.updated_at),
})

const normalizeWithSecret = (w: any): WebhookWithSecret => ({ ...normalize(w), secret: w?.secret ?? '' })

export interface WebhookWriteFields {
  name: string
  url: string
  direction: WebhookDirection
  enabled: boolean
}

// GET /api/v5/webhooks (paged; the settings section shows the first page)
export const getWebhooks = async (orgId: string): Promise<Webhook[]> => {
  const res = await getRequest({
    endpoint: 'api/v5/webhooks?limit=100',
    header: orgHeader(orgId),
  })
  const list = res?.data?.data ?? []
  return list.map(normalize)
}

// POST /api/v5/webhooks -> the only response besides a rotation that reveals the secret
export const createWebhook = async (orgId: string, fields: WebhookWriteFields): Promise<WebhookWithSecret> => {
  const res = await postRequest({
    endpoint: 'api/v5/webhooks',
    header: orgHeader(orgId),
    data: { orgId, ...fields },
  })
  return normalizeWithSecret(res?.data?.data)
}

// PUT /api/v5/webhooks/{id}
export const updateWebhook = async (
  id: string,
  orgId: string,
  fields: Partial<WebhookWriteFields>,
): Promise<Webhook> => {
  const res = await putRequest({
    endpoint: `api/v5/webhooks/${id}`,
    header: orgHeader(orgId),
    data: fields,
  })
  return normalize(res?.data?.data)
}

// POST /api/v5/webhooks/{id}/secret -> the new secret; the old one stops working
export const rotateWebhookSecret = async (id: string, orgId: string): Promise<WebhookWithSecret> => {
  const res = await postRequest({
    endpoint: `api/v5/webhooks/${id}/secret`,
    header: orgHeader(orgId),
    data: {},
  })
  return normalizeWithSecret(res?.data?.data)
}

// DELETE /api/v5/webhooks/{id} -> 204
export const deleteWebhook = async (id: string, orgId: string): Promise<void> => {
  await deleteRequest({
    endpoint: `api/v5/webhooks/${id}`,
    header: orgHeader(orgId),
  })
}

// Changeset errors arrive as JSON:API errors; surface the first readable one.
export const webhookErrorMessage = (e: any, fallback: string): string => {
  const first = e?.response?.data?.errors?.[0]
  return first?.detail || first?.title || e?.message || fallback
}
